import { useEffect, useState } from 'react';
import { Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { apiFetch } from '../lib/apiClient';

const MIN_FEE = 0.5;
const MAX_FEE = 5;
const DEFAULT_FEE = 1.5;

export default function SkipTheLineSettings({ truck, readOnly = false, onSaved }) {
  const [enabled, setEnabled] = useState(Boolean(truck?.skip_the_line_enabled));
  const [fee, setFee] = useState(String(truck?.skip_the_line_fee ?? DEFAULT_FEE));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(Boolean(truck?.skip_the_line_enabled));
    setFee(Number(truck?.skip_the_line_fee ?? DEFAULT_FEE).toFixed(2));
  }, [truck?.truck_id, truck?.skip_the_line_enabled, truck?.skip_the_line_fee]);

  const feeNumber = Number.parseFloat(fee);
  const feeValid = Number.isFinite(feeNumber) && feeNumber >= MIN_FEE && feeNumber <= MAX_FEE;

  const handleSave = async () => {
    if (!truck || readOnly || !feeValid) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/trucks/${truck.truck_id}/skip-the-line`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, fee: Math.round(feeNumber * 100) / 100 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save skip the line settings');
      }
      onSaved?.(data);
      toast.success('Skip the line settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save skip the line settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border p-4 md:p-6 space-y-4" data-testid="skip-the-line-settings">
      <div>
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary fill-current" />
          Skip the Line
        </h3>
        <p className="text-sm text-muted-foreground">
          Students pay this to pick up in 5 or 10 minutes. The fee goes to SkipdQ.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="skip-the-line-enabled" className="text-sm font-medium">
          Enable skip the line orders
        </Label>
        <Switch
          id="skip-the-line-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-1.5 max-w-[12rem]">
        <Label htmlFor="skip-the-line-fee" className="text-sm font-medium">Fee</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
          <Input
            id="skip-the-line-fee"
            type="number"
            inputMode="decimal"
            min={MIN_FEE}
            max={MAX_FEE}
            step="0.05"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            disabled={readOnly}
            className="pl-6"
          />
        </div>
        {!feeValid && (
          <p className="text-xs text-destructive">
            Enter an amount between ${MIN_FEE.toFixed(2)} and ${MAX_FEE.toFixed(2)}.
          </p>
        )}
      </div>

      {!readOnly && (
        <Button onClick={handleSave} disabled={saving || !feeValid} data-testid="save-skip-the-line-btn">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    </div>
  );
}
